import React, { JSX } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import LoadingSpinner from '../common/loading/LoadingSprinner';

interface ProtectedRouteProps {
  element: JSX.Element;
  allowedRoles?: string[];
  userRole?: string | string[];
  isAuthenticated: boolean;
  /**
   * Multi-business support (Phase 1) — per approved_architecture.md §4.3.
   * When true, the route requires an active business scope. The backend's
   * requireBusinessScope middleware (from @aplicy-com/aplicy-auth-middleware
   * v1.2.0+) provides server-side enforcement; this flag provides the
   * client-side gate.
   */
  requireBusinessScope?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  element,
  allowedRoles,
  userRole,
  isAuthenticated,
  requireBusinessScope = false,
}) => {
  const { activeBusinessId, loading } = useAuth();

  // if no role restrictions, it's public
  if (!allowedRoles || allowedRoles.length === 0) {
    return element;
  }

  // Wait for the session to hydrate before deciding to redirect. On a hard
  // load (deep link / full page refresh), useAuth().isAuthenticated is still
  // false while /auth/me resolves; without this gate role-gated pages would
  // bounce to /home before the session is known. Mirrors the loading gate in
  // frontend/auth/ProtectedRoute.tsx (F1 regression — see report §3).
  if (loading) {
    return <LoadingSpinner label="Loading.." />;
  }

  // user not logged in
  if (!isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  // user doesn't have the right role
  const roles = Array.isArray(userRole) ? userRole : userRole ? [userRole] : [];
  const hasAccess = roles.some((r) => allowedRoles.includes(r));
  if (!hasAccess) {
    return <Navigate to="/home" replace />;
  }

  // Multi-business scope gate (Phase 1) — client-side equivalent of
  // requireBusinessScope from @aplicy-com/aplicy-auth-middleware v1.2.0+.
  if (requireBusinessScope && !activeBusinessId) {
    return <Navigate to="/home" replace />;
  }

  // user allowed
  return element;
};

export default ProtectedRoute;
